import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { EnvironmentService } from '../../services/environment.service';

/**
 * Warnbanner für Nicht-Live-Umgebungen (Test/Dev). Auf Live rendert die
 * Komponente nichts.
 *
 * `variant="sidebar"` — im Fluss, für den sidebarExtra-Slot der App-Shell.
 * `variant="floating"` — fixiert am oberen Viewport-Rand, für die Auth-Seiten
 * außerhalb der App-Shell.
 *
 * Das Banner ist bewusst nicht wegklickbar: Es soll dauerhaft stören.
 *
 * `role="note"` statt `role="status"`: Der Text ist statisch. Als Live-Region
 * würden Screenreader ihn bei jedem Seitenaufbau erneut ansagen.
 *
 * Aufrufer sollten die Komponente nur einhängen, wenn `EnvironmentService.kennzeichen`
 * gesetzt ist — sonst bleibt auf Live ein leerer Host-Tag im DOM, der `:empty`-Regeln
 * des umgebenden Containers aushebelt (siehe DefaultLayoutComponent).
 */
@Component({
  selector: 'app-environment-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (kennzeichen; as kz) {
      <div
        class="env-banner"
        [class.env-banner--test]="kz.variante === 'test'"
        [class.env-banner--dev]="kz.variante === 'dev'"
        [class.env-banner--floating]="variant() === 'floating'"
        role="note"
      >{{ kz.kuerzel }}</div>
    }
  `,
  styles: [
    `
    .env-banner {
      position: relative;
      padding: 9px 0;
      text-align: center;
      font-family: 'Titillium Web', Calibri, sans-serif;
      font-weight: 700;
      font-size: 13px;
      letter-spacing: 2.5px;
      // Hebt das seitliche Padding des sidebarExtra-Slots (0.75rem) auf, damit
      // die Warnstreifen bis an den Sidebar-Rand laufen.
      margin: 0 -0.75rem 0.5rem;
    }

    // Warnstreifen oben und unten.
    .env-banner::before,
    .env-banner::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      height: 6px;
    }
    .env-banner::before { top: 0; }
    .env-banner::after  { bottom: 0; }

    // Flächenfarben sind deckend (nicht rgba): Im floating-Modus liegt das
    // Banner über der Login-Seite, deren rechte Hälfte weiß ist. Die Werte
    // entsprechen den Entwurfsfarben über dem Sidebar-Indigo #252049.
    .env-banner--test { background-color: #48313D; color: #FFB020; }
    .env-banner--test::before,
    .env-banner--test::after {
      background: repeating-linear-gradient(-45deg, #FFB020 0 8px, #252049 8px 16px);
    }

    .env-banner--dev { background-color: #253960; color: #4DD8EE; }
    .env-banner--dev::before,
    .env-banner--dev::after {
      background: repeating-linear-gradient(-45deg, #22D3EE 0 8px, #252049 8px 16px);
    }

    .env-banner--floating {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1100;
      margin: 0;
    }
  `,
  ],
})
export class EnvironmentBannerComponent {
  readonly variant = input<'sidebar' | 'floating'>('sidebar');
  readonly kennzeichen = inject(EnvironmentService).kennzeichen;
}
